import { create } from 'zustand';
import { initializeCore } from '@/core';
import type {
  HeightmapAsset,
  LedSpec,
  PanelDesign,
  PanelGeometrySpec,
  ParamValues,
  PatternLayer,
  ReliefFinish,
  TextureSource,
  WallGrid,
} from '@/core/types';
import type { TextureGenerationResult } from '@/core/texture/provider';
import { createDefaultDesign, createLayer } from '@/core/design/defaults';
import { parseDesign, serializeDesign } from '@/core/design/serialize';
import { getPreset } from '@/core/texture/pack';
import { clamp } from '@/core/math/scalar';
import { CONSTRAINTS } from '@/core/design/constraints';

/**
 * Application state.
 *
 * The store holds exactly one thing that matters: the `PanelDesign`. Everything
 * on screen — the viewport, the thumbnails, the statistics, the export — is
 * derived from it, so there is no second copy of the truth to drift out of sync.
 */

/**
 * Filling the registries here rather than only in `main.tsx` is deliberate:
 * this module restores a saved design while it is being evaluated, which is
 * before any entry-point statement runs. Without this the restored design
 * would have every layer dropped as "unknown pattern". `initializeCore` is
 * idempotent, so the entry point may still call it.
 */
initializeCore();

const STORAGE_KEY = 'organic3d.design';

export type WorkspaceStep = 'texture' | 'panel' | 'export';

interface DesignState {
  design: PanelDesign;
  step: WorkspaceStep;
  /** Index into the wall layout, or `null` for the whole wall. */
  selectedPanel: number | null;
  /** Notes from the last load or generation, shown as a dismissible banner. */
  notices: string[];

  setStep(step: WorkspaceStep): void;
  setSelectedPanel(index: number | null): void;
  dismissNotices(): void;

  rename(name: string): void;
  setGrid(grid: Partial<WallGrid>): void;
  setPanelSpec(spec: Partial<PanelGeometrySpec>): void;
  setFinish(finish: Partial<ReliefFinish>): void;
  setLed(led: Partial<LedSpec>): void;

  applyPreset(presetId: string): void;
  applyGenerated(result: TextureGenerationResult, source: Partial<TextureSource>): void;
  applyImage(asset: HeightmapAsset, fit: string): void;

  setLayers(layers: PatternLayer[]): void;
  addLayer(patternId: string): void;
  updateLayer(id: string, patch: Partial<PatternLayer>): void;
  updateLayerParams(id: string, params: ParamValues): void;
  removeLayer(id: string): void;
  moveLayer(id: string, direction: -1 | 1): void;

  loadDesign(json: string): void;
  exportDesignJson(): string;
  reset(): void;
}

/** Marks the design as hand-edited once the user touches the layer stack. */
function asCustom(source: TextureSource, layers: PatternLayer[]): TextureSource {
  if (source.kind === 'custom') return { ...source, layers };
  return { kind: 'custom', label: `${source.label} (edited)`, layers };
}

function restore(): PanelDesign {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return createDefaultDesign();
    return parseDesign(stored).design;
  } catch {
    // A corrupt or blocked store must never stop the app from opening.
    return createDefaultDesign();
  }
}

let persistTimer: ReturnType<typeof setTimeout> | undefined;

function persist(design: PanelDesign): void {
  clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, serializeDesign(design));
    } catch {
      // Quota exceeded (a large imported bitmap) — the session still works.
    }
  }, 500);
}

export const useDesignStore = create<DesignState>((set, get) => {
  /** Applies a change to the design and schedules persistence. */
  const commit = (update: (design: PanelDesign) => PanelDesign): void => {
    set((state) => {
      const design = update(state.design);
      persist(design);
      return { design };
    });
  };

  return {
    design: restore(),
    step: 'texture',
    selectedPanel: null,
    notices: [],

    setStep: (step) => set({ step }),
    setSelectedPanel: (selectedPanel) => set({ selectedPanel }),
    dismissNotices: () => set({ notices: [] }),

    rename: (name) => commit((design) => ({ ...design, name })),

    setGrid: (grid) =>
      commit((design) => ({
        ...design,
        grid: {
          columns: clamp(
            grid.columns ?? design.grid.columns,
            CONSTRAINTS.columns.min,
            CONSTRAINTS.columns.max,
          ),
          rows: clamp(grid.rows ?? design.grid.rows, CONSTRAINTS.rows.min, CONSTRAINTS.rows.max),
        },
      })),

    setPanelSpec: (spec) =>
      commit((design) => ({
        ...design,
        panel: { ...design.panel, ...spec, edge: { ...design.panel.edge, ...(spec.edge ?? {}) } },
      })),

    setFinish: (finish) =>
      commit((design) => ({ ...design, finish: { ...design.finish, ...finish } })),

    setLed: (led) => commit((design) => ({ ...design, led: { ...design.led, ...led } })),

    applyPreset: (presetId) => {
      const preset = getPreset(presetId);
      if (!preset) return;

      commit((design) => ({
        ...design,
        source: { kind: 'pack', label: preset.name, presetId, layers: preset.build() },
        finish: { ...design.finish, ...preset.finish },
        panel: { ...design.panel, reliefDepth: preset.suggestedDepth },
      }));
      set({ notices: [] });
    },

    applyGenerated: (result, source) => {
      commit((design) => ({
        ...design,
        source: {
          kind: 'ai',
          label: result.label,
          ...source,
          layers: result.layers,
        },
        finish: { ...design.finish, ...result.finish },
        panel: {
          ...design.panel,
          reliefDepth: result.suggestedDepth ?? design.panel.reliefDepth,
        },
        assets: [...design.assets, ...(result.assets ?? [])],
      }));
      set({ notices: result.notes });
    },

    applyImage: (asset, fit) => {
      commit((design) => ({
        ...design,
        // Replace an asset with the same name rather than accumulating copies.
        assets: [...design.assets.filter((existing) => existing.id !== asset.id), asset],
        source: {
          kind: 'image',
          label: asset.name,
          layers: [createLayer('heightmap', { params: { asset: asset.id, fit, tile: 'mirror' } })],
        },
      }));
      set({
        notices: [
          `Imported "${asset.name}" at ${asset.width}x${asset.height}.`,
          'Bright areas become high relief. Use Levels to change how it reads.',
        ],
      });
    },

    setLayers: (layers) =>
      commit((design) => ({ ...design, source: asCustom(design.source, layers) })),

    addLayer: (patternId) =>
      commit((design) => ({
        ...design,
        source: asCustom(design.source, [
          ...design.source.layers,
          createLayer(patternId, {
            blend: design.source.layers.length === 0 ? 'replace' : 'overlay',
          }),
        ]),
      })),

    updateLayer: (id, patch) =>
      commit((design) => ({
        ...design,
        source: asCustom(
          design.source,
          design.source.layers.map((layer) =>
            layer.id === id
              ? {
                  ...layer,
                  ...patch,
                  transform: { ...layer.transform, ...(patch.transform ?? {}) },
                }
              : layer,
          ),
        ),
      })),

    updateLayerParams: (id, params) =>
      commit((design) => ({
        ...design,
        source: asCustom(
          design.source,
          design.source.layers.map((layer) =>
            layer.id === id ? { ...layer, params: { ...layer.params, ...params } } : layer,
          ),
        ),
      })),

    removeLayer: (id) =>
      commit((design) => {
        const layers = design.source.layers.filter((layer) => layer.id !== id);
        // The stack must never be empty; a flat layer is the neutral element.
        return {
          ...design,
          source: asCustom(design.source, layers.length > 0 ? layers : [createLayer('flat')]),
        };
      }),

    moveLayer: (id, direction) =>
      commit((design) => {
        const layers = [...design.source.layers];
        const index = layers.findIndex((layer) => layer.id === id);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= layers.length) return design;

        [layers[index], layers[target]] = [layers[target], layers[index]];
        return { ...design, source: asCustom(design.source, layers) };
      }),

    loadDesign: (json) => {
      const { design, warnings } = parseDesign(json);
      persist(design);
      set({ design, notices: warnings, selectedPanel: null });
    },

    exportDesignJson: () => serializeDesign(get().design),

    reset: () => {
      const design = createDefaultDesign();
      persist(design);
      set({ design, notices: [], selectedPanel: null, step: 'texture' });
    },
  };
});
